import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@material-ui/styles';
import _ from 'lodash';
import Button from '../../../atoms/buttons/Button';
import BlockedUserItem from '../../../atoms/BlockedUserItem';
import { displayUserLocation } from '../../../common';
import { IBlockedProps, IUserProfile } from './types/Blocked';
import { getBorders } from '../../../style';
import colors from '../../../theme/colors';
import Loading from '../../../atoms/Loading';
import SkeletonLoader from '../../../atoms/SkeletonLoader';
import { isDarkMode } from '../../../utils/helpers';
import { loadMoreButtonStyle } from '../../common/CommonStyle';

const ButtonContainer = styled('div')({
  margin: '16px auto',
  maxWidth: '200px',
  textAlign: 'center',
});

const NoTitle = styled('p')(() => ({
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '18px',
  letterSpacing: '-0.26px',
  padding: '10px 0px 10px 0px',
  borderBottom: getBorders().DEFAULT,
  margin: 0,
  color: isDarkMode() ? '#CFCFCF' : colors.light.gray.light,
}));

const Blocked = ({
  pageId,
  blockedItems,
  handleOnClick,
  isFetching,
  loadMore,
  moreExists,
}: IBlockedProps): ReactElement => {
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  const myProfile = useSelector(state => state.profile?.profile?.data);

  const getInfoText = (profile: IUserProfile, otherProfile: IUserProfile | null) => {
    if (!profile || !otherProfile) return '';

    const userLoc = {
      country: profile.country,
      region: profile.region,
      city: profile.city,
    };

    const offerUserLoc = {
      country: otherProfile.country,
      region: otherProfile.region,
      city: otherProfile.city,
    };

    return `${otherProfile.age} • ${displayUserLocation(userLoc, offerUserLoc)}`;
  };

  const renderMoreExists = () => {
    if (!moreExists) return;

    return (
      <>
        {isFetching && !_.isEmpty(blockedItems) ? (
          <ButtonContainer>
            <Loading />
          </ButtonContainer>
        ) : (
          <ButtonContainer>
            <Button
              buttonType="loadMore"
              fit
              onClick={loadMore}
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              customStyle={loadMoreButtonStyle}
            >
              Load More...
            </Button>
          </ButtonContainer>
        )}
      </>
    );
  };

  return (
    <>
      {_.isEmpty(blockedItems) && isFetching && (
        <SkeletonLoader width="100%">
          <rect x="0" y="0" rx="5" ry="5" width="60" height="60" />
          <rect x="80" y="17" rx="4" ry="4" width="380" height="6" />
          <rect x="80" y="40" rx="3" ry="3" width="100" height="4" />
        </SkeletonLoader>
      )}

      {!isFetching && _.isEmpty(blockedItems) && <NoTitle>{`You have no ${pageId} profiles`}</NoTitle>}

      {!_.isEmpty(blockedItems) &&
        blockedItems.map((userData, index: number) => {
          const account = userData?.to_account?.data;
          const profile = account && account.profile ? account.profile.data : null;

          return profile ? (
            <BlockedUserItem
              key={`${pageId}_${index}`}
              hashId={userData.to_account_hash_id}
              avatar={profile?.avatar?.data?.urls?.thumbs}
              username={account.username}
              userDetails={getInfoText(myProfile, profile)}
              onUnblock={handleOnClick}
              buttonTitle={pageId === 'dismissed' ? 'Unhide' : 'Unblock'}
            />
          ) : null;
        })}
      {renderMoreExists()}
    </>
  );
};

export default Blocked;
